import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Ban, Copy, KeyRound, RefreshCw, Search, Trash2, UserCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '../lib/api'
import { useSortState } from '../lib/useSortState'
import {
  Badge,
  Button,
  Chip,
  ChipList,
  EmptyTableRow,
  ListToolbar,
  Modal,
  SortableTh,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea
} from '../components/ui'

type ParentPortalStatus = 'active' | 'pending' | 'expired' | 'none'
type SortKey = 'parent' | 'loginName' | 'status' | 'registeredAt' | 'lastLogin' | 'children' | 'invitedAt' | 'invitedBy'

interface LinkedChild {
  sophomorixUnid: string
  Vorname: string
  Name: string
  Klasse: string
}

interface ParentPortalRow {
  user_id: number | null
  email: string
  username: string | null
  first_name: string | null
  last_name: string | null
  is_active: boolean | number | null
  status: ParentPortalStatus
  registered_at: string | null
  last_login: string | null
  invited_at: string | null
  expires_at: string | null
  accepted_at: string | null
  elternbrief_id: number | null
  elternbrief_titel: string | null
  invited_by_name: string | null
  linked_children: LinkedChild[]
  linked_children_count: number
}

interface ParentPortalStats {
  active: number
  pending: number
  expired: number
  none: number
  total: number
}

const DEFAULT_STATS: ParentPortalStats = {
  active: 0,
  pending: 0,
  expired: 0,
  none: 0,
  total: 0
}

function formatDateTime(value: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' }).format(date)
}

function statusLabel(row: ParentPortalRow): string {
  if (row.user_id && !isAccountActive(row)) return 'Gesperrt'
  if (row.status === 'active') return 'Registriert'
  if (row.status === 'pending') return 'Einladung offen'
  if (row.status === 'expired') return 'Abgelaufen'
  return 'Kein Konto'
}

function statusVariant(row: ParentPortalRow): 'success' | 'warning' | 'danger' | 'neutral' {
  if (row.user_id && !isAccountActive(row)) return 'danger'
  if (row.status === 'active') return 'success'
  if (row.status === 'pending') return 'warning'
  if (row.status === 'expired') return 'danger'
  return 'neutral'
}

function isAccountActive(row: ParentPortalRow): boolean {
  return row.is_active !== false && row.is_active !== 0
}

function isRowBlocked(row: ParentPortalRow): boolean {
  return Boolean(row.user_id) && !isAccountActive(row)
}

function canManageAccount(row: ParentPortalRow): boolean {
  return Boolean(row.user_id)
}

async function copyText(value: string, successMessage: string) {
  try {
    await navigator.clipboard.writeText(value)
    toast.success(successMessage)
  } catch {
    toast.error('Kopieren fehlgeschlagen')
  }
}

function sortCompare(rowA: ParentPortalRow, rowB: ParentPortalRow, key: SortKey): number {
  const toMillis = (value: string | null) => {
    if (!value) return 0
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? 0 : parsed
  }
  switch (key) {
    case 'parent': {
      const nameA = `${rowA.last_name || ''} ${rowA.first_name || ''} ${rowA.email || ''}`.trim()
      const nameB = `${rowB.last_name || ''} ${rowB.first_name || ''} ${rowB.email || ''}`.trim()
      return nameA.localeCompare(nameB, 'de', { sensitivity: 'base' })
    }
    case 'loginName':
      return String(rowA.username || '').localeCompare(String(rowB.username || ''), 'de', { sensitivity: 'base' })
    case 'status':
      return statusLabel(rowA).localeCompare(statusLabel(rowB), 'de', { sensitivity: 'base' })
    case 'registeredAt':
      return toMillis(rowA.registered_at) - toMillis(rowB.registered_at)
    case 'lastLogin':
      return toMillis(rowA.last_login) - toMillis(rowB.last_login)
    case 'children':
      return (rowA.linked_children_count || 0) - (rowB.linked_children_count || 0)
    case 'invitedAt':
      return toMillis(rowA.invited_at) - toMillis(rowB.invited_at)
    case 'invitedBy':
      return String(rowA.invited_by_name || '').localeCompare(String(rowB.invited_by_name || ''), 'de', {
        sensitivity: 'base'
      })
    default:
      return 0
  }
}

export default function AdminParentPortal() {
  const [rows, setRows] = useState<ParentPortalRow[]>([])
  const [stats, setStats] = useState<ParentPortalStats>(DEFAULT_STATS)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'' | ParentPortalStatus>('')
  const [actionLoadingKey, setActionLoadingKey] = useState<string | null>(null)
  const [resetPasswordResult, setResetPasswordResult] = useState<{
    username: string
    email: string
    temporary_password: string
  } | null>(null)
  const { sortKey, direction, setSort } = useSortState<SortKey>('registeredAt', 'desc')
  const [parentPortalEnabled, setParentPortalEnabled] = useState(true)

  useEffect(() => {
    void (async () => {
      try {
        const response = await api.get('/settings/parent-portal')
        setParentPortalEnabled(Boolean(response.data?.data?.enabled))
      } catch {
        /* Admin-only; keep default enabled on error */
      }
    })()
  }, [])

  const fetchOverview = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/users/parent-portal-overview', {
        params: {
          search: search || undefined,
          status: statusFilter || undefined,
          limit: 250,
          offset: 0
        }
      })
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Fehler beim Laden der Übersicht')
      }
      setRows(Array.isArray(response.data.data) ? response.data.data : [])
      setStats({
        ...DEFAULT_STATS,
        ...(response.data.stats || {})
      })
    } catch (fetchError: any) {
      const message = fetchError?.response?.data?.error || fetchError?.message || 'Fehler beim Laden der Übersicht'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter])

  useEffect(() => {
    void fetchOverview()
  }, [fetchOverview])

  const sortedRows = useMemo(() => {
    if (!sortKey) return [...rows]
    return [...rows].sort((a, b) => {
      const compared = sortCompare(a, b, sortKey)
      return direction === 'asc' ? compared : -compared
    })
  }, [rows, sortKey, direction])

  const runAccountAction = async (
    row: ParentPortalRow,
    actionKey: string,
    request: () => Promise<void>,
    successMessage?: string
  ) => {
    if (!row.user_id) return
    setActionLoadingKey(`${row.user_id}:${actionKey}`)
    try {
      await request()
      if (successMessage) toast.success(successMessage)
      await fetchOverview()
    } catch (actionError: any) {
      const message =
        actionError?.response?.data?.message ||
        actionError?.response?.data?.error ||
        actionError?.message ||
        'Aktion fehlgeschlagen'
      toast.error(message)
    } finally {
      setActionLoadingKey(null)
    }
  }

  const handleResetPassword = async (row: ParentPortalRow) => {
    if (!row.user_id) return
    if (!window.confirm(`Passwort für ${row.email} wirklich zurücksetzen?`)) return

    setActionLoadingKey(`${row.user_id}:reset-password`)
    try {
      const response = await api.post(`/users/parent-portal/${row.user_id}/reset-password`)
      const data = response.data?.data
      if (!data?.temporary_password) {
        throw new Error('Kein temporäres Passwort erhalten')
      }
      setResetPasswordResult({
        username: data.username || row.username || '',
        email: data.email || row.email,
        temporary_password: data.temporary_password
      })
      toast.success('Passwort wurde zurückgesetzt')
      await fetchOverview()
    } catch (actionError: any) {
      const message =
        actionError?.response?.data?.message ||
        actionError?.response?.data?.error ||
        actionError?.message ||
        'Passwort konnte nicht zurückgesetzt werden'
      toast.error(message)
    } finally {
      setActionLoadingKey(null)
    }
  }

  const handleToggleBlocked = async (row: ParentPortalRow) => {
    if (!row.user_id) return
    const blocked = !isRowBlocked(row)
    const question = blocked
      ? `Elternkonto ${row.email} wirklich sperren?`
      : `Elternkonto ${row.email} wirklich entsperren?`
    if (!window.confirm(question)) return

    await runAccountAction(
      row,
      blocked ? 'block' : 'unblock',
      async () => {
        await api.patch(`/users/parent-portal/${row.user_id}/status`, { blocked })
      },
      blocked ? 'Elternkonto wurde gesperrt' : 'Elternkonto wurde entsperrt'
    )
  }

  const handleDeleteAccount = async (row: ParentPortalRow) => {
    if (!row.user_id) return
    if (
      !window.confirm(
        `Elternkonto ${row.email} endgültig löschen? Das Konto muss danach neu eingeladen werden.`
      )
    ) {
      return
    }

    await runAccountAction(
      row,
      'delete',
      async () => {
        await api.delete(`/users/parent-portal/${row.user_id}`)
      },
      'Elternkonto wurde gelöscht'
    )
  }

  return (
    <div className="space-y-4">
      <ListToolbar
        title="Elternportal"
        description="Übersicht über Registrierungen und Einladungsstatus im Elternfrontend."
        trailing={
          <>
            <Badge variant="success">Registriert: {stats.active}</Badge>
            <Badge variant="warning">Offen: {stats.pending}</Badge>
            <Badge variant="danger">Abgelaufen: {stats.expired}</Badge>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                className="input-field min-h-[2.25rem] w-full min-w-[14rem] py-2 pl-10 text-sm"
                placeholder="Name oder E-Mail suchen"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <select
              className="input-field min-h-[2.25rem] py-2 text-sm"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as '' | ParentPortalStatus)}
            >
              <option value="">Alle Status</option>
              <option value="active">Registriert</option>
              <option value="pending">Einladung offen</option>
              <option value="expired">Abgelaufen</option>
              <option value="none">Kein Konto</option>
            </select>
            <Button type="button" variant="outline" size="sm" onClick={() => void fetchOverview()}>
              <RefreshCw className="mr-1 h-4 w-4" />
              Aktualisieren
            </Button>
          </>
        }
      />

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/20 dark:text-red-300">
          {error}
        </div>
      ) : null}

      {!parentPortalEnabled ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/70 dark:bg-amber-950/20 dark:text-amber-200">
          Das Elternportal ist deaktiviert. Eltern können sich nicht anmelden; neue Einladungen sind
          gesperrt. Elternbrief-Links per E-Mail funktionieren weiter.{' '}
          <Link to="/settings?tab=system-admin" className="font-medium underline hover:no-underline">
            In den Einstellungen aktivieren
          </Link>
        </div>
      ) : null}

      <TableScrollArea>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTh sortKey="parent" activeKey={sortKey} direction={direction} onSort={setSort}>
                Elternkonto
              </SortableTh>
              <SortableTh sortKey="loginName" activeKey={sortKey} direction={direction} onSort={setSort}>
                Anmeldename
              </SortableTh>
              <SortableTh sortKey="status" activeKey={sortKey} direction={direction} onSort={setSort}>
                Status
              </SortableTh>
              <SortableTh sortKey="registeredAt" activeKey={sortKey} direction={direction} onSort={setSort}>
                Registrierung
              </SortableTh>
              <SortableTh sortKey="lastLogin" activeKey={sortKey} direction={direction} onSort={setSort}>
                Letzter Login
              </SortableTh>
              <SortableTh sortKey="children" activeKey={sortKey} direction={direction} onSort={setSort}>
                Verknüpfte Kinder
              </SortableTh>
              <SortableTh sortKey="invitedAt" activeKey={sortKey} direction={direction} onSort={setSort}>
                Einladung
              </SortableTh>
              <SortableTh sortKey="invitedBy" activeKey={sortKey} direction={direction} onSort={setSort}>
                Eingeladen von
              </SortableTh>
              <TableHeaderCell className="text-right">Aktionen</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <EmptyTableRow colSpan={9} title="Lädt..." description="Elternportal-Daten werden geladen." />
            ) : sortedRows.length === 0 ? (
              <EmptyTableRow colSpan={9} title="Keine Einträge gefunden" description="Filter oder Suche anpassen." />
            ) : (
              sortedRows.map((row) => (
                <TableRow key={`${row.email}-${row.user_id || 'invite'}`}>
                  <TableCell className="whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {[row.first_name, row.last_name].filter(Boolean).join(' ') || row.username || 'Unbekannt'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{row.email}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {row.username ? (
                      <div className="space-y-1">
                        <div className="font-mono text-sm text-gray-900 dark:text-gray-100">{row.username}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Anmeldung auch mit E-Mail möglich
                        </div>
                        <Button
                          type="button"
                          size="xs"
                          variant="outline"
                          onClick={() => void copyText(row.username || '', 'Anmeldename kopiert')}
                        >
                          <Copy className="mr-1 h-3.5 w-3.5" />
                          Kopieren
                        </Button>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(row)}>{statusLabel(row)}</Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                    {formatDateTime(row.registered_at)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                    {formatDateTime(row.last_login)}
                  </TableCell>
                  <TableCell>
                    <ChipList empty="Keine Verknüpfung">
                      {row.linked_children?.map((child) => (
                        <Chip key={`${child.sophomorixUnid}-${child.Klasse}`} variant="outline">
                          {child.Vorname} {child.Name} ({child.Klasse})
                        </Chip>
                      ))}
                    </ChipList>
                  </TableCell>
                  <TableCell className="min-w-[14rem]">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Gesendet: {formatDateTime(row.invited_at)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Läuft ab: {formatDateTime(row.expires_at)}
                    </div>
                    {row.elternbrief_id ? (
                      <Link
                        className="text-xs text-primary-700 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        to={`/elternbriefe/${row.elternbrief_id}`}
                      >
                        {row.elternbrief_titel || `Elternbrief #${row.elternbrief_id}`}
                      </Link>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm text-gray-700 dark:text-gray-300">
                    {row.invited_by_name || '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    {canManageAccount(row) ? (
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          type="button"
                          size="xs"
                          variant="outline"
                          disabled={actionLoadingKey === `${row.user_id}:reset-password`}
                          onClick={() => void handleResetPassword(row)}
                        >
                          <KeyRound className="mr-1 h-3.5 w-3.5" />
                          Passwort
                        </Button>
                        <Button
                          type="button"
                          size="xs"
                          variant={isRowBlocked(row) ? 'secondary' : 'outline'}
                          disabled={
                            actionLoadingKey === `${row.user_id}:block` ||
                            actionLoadingKey === `${row.user_id}:unblock`
                          }
                          onClick={() => void handleToggleBlocked(row)}
                        >
                          {isRowBlocked(row) ? (
                            <>
                              <UserCheck className="mr-1 h-3.5 w-3.5" />
                              Entsperren
                            </>
                          ) : (
                            <>
                              <Ban className="mr-1 h-3.5 w-3.5" />
                              Sperren
                            </>
                          )}
                        </Button>
                        <Button
                          type="button"
                          size="xs"
                          variant="danger"
                          disabled={actionLoadingKey === `${row.user_id}:delete`}
                          onClick={() => void handleDeleteAccount(row)}
                        >
                          <Trash2 className="mr-1 h-3.5 w-3.5" />
                          Löschen
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500 dark:text-gray-400">Noch kein Konto</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableScrollArea>

      <Modal
        isOpen={Boolean(resetPasswordResult)}
        onClose={() => setResetPasswordResult(null)}
        title="Temporäres Passwort"
        description="Bitte dem Elternteil mitteilen. Beim nächsten Login kann ein neues Passwort gesetzt werden."
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setResetPasswordResult(null)}>
              Schließen
            </Button>
            {resetPasswordResult ? (
              <Button
                type="button"
                onClick={() =>
                  void copyText(
                    resetPasswordResult.temporary_password,
                    'Temporäres Passwort kopiert'
                  )
                }
              >
                <Copy className="mr-1 h-4 w-4" />
                Passwort kopieren
              </Button>
            ) : null}
          </div>
        }
      >
        {resetPasswordResult ? (
          <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
            <div>
              <span className="font-medium text-gray-900 dark:text-gray-100">E-Mail:</span>{' '}
              {resetPasswordResult.email}
            </div>
            <div>
              <span className="font-medium text-gray-900 dark:text-gray-100">Anmeldename:</span>{' '}
              <span className="font-mono">{resetPasswordResult.username}</span>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/60 dark:bg-amber-950/20">
              <div className="text-xs font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300">
                Temporäres Passwort
              </div>
              <div className="mt-1 font-mono text-base text-amber-950 dark:text-amber-100">
                {resetPasswordResult.temporary_password}
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
